import { ADMIN_TABLE_PAGE_SIZE, sanitiseSearch } from "@/features/admin/tables";
import { supabaseClient } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";

export const ENTRY_STATUSES = ["draft", "submitted", "voided", "skipped"] as const;
export type EntryStatus = (typeof ENTRY_STATUSES)[number];

export interface AdminEntryFilters {
    q?: string;
    diaryId?: string;
    trackerId?: string;
    entryId?: string;
    organisationId?: string;
    status?: EntryStatus;
    page?: number;
    pageSize?: number;
}

export interface AdminEntryRow {
    entry_id: string;
    tracker_id: string | null;
    diary_id: string | null;
    form_id: string | null;
    form_name: string | null;
    patient_name: string;
    hospital_number: string | null;
    organisation_name: string | null;
    organisation_id: string | null;
    status: string;
    entry_time: string | null;
    submitted_at: string | null;
    submission_source: string | null;
    timing_label: string | null;
}

export interface AdminEntriesResult {
    entries: AdminEntryRow[];
    totalCount: number;
    page: number;
    pageSize: number;
}

async function diaryIdsMatchingPatientSearch(q: string): Promise<string[]> {
    const { data, error } = await supabaseClient
        .from("diaries_view")
        .select("diary_id")
        .or(`patient_first_name.ilike.%${q}%,patient_last_name.ilike.%${q}%,hospital_number.ilike.%${q}%`)
        .limit(500);
    if (error) throw error;
    return (data ?? []).map((row) => row.diary_id as string).filter(Boolean);
}

async function trackerIdsForDiaryAllowList(diaryIds: string[]): Promise<string[]> {
    if (diaryIds.length === 0) return [];
    const { data, error } = await supabaseClient.from("trackers").select("tracker_id").in("diary_id", diaryIds);
    if (error) throw error;
    return (data ?? []).map((row) => row.tracker_id as string).filter(Boolean);
}

export async function fetchAdminEntries(filters: AdminEntryFilters): Promise<AdminEntriesResult> {
    const page = Math.max(1, filters.page ?? 1);
    const pageSize = Math.max(1, filters.pageSize ?? ADMIN_TABLE_PAGE_SIZE);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let trackerIdAllowList: string[] | null = null;

    if (filters.trackerId) {
        trackerIdAllowList = [filters.trackerId];
    }

    let diaryIdAllowList: string[] | null = filters.diaryId ? [filters.diaryId] : null;

    const q = filters.q ? sanitiseSearch(filters.q) : "";
    if (q) {
        const matched = await diaryIdsMatchingPatientSearch(q);
        if (matched.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
        diaryIdAllowList = diaryIdAllowList === null ? matched : diaryIdAllowList.filter((id) => matched.includes(id));
        if (diaryIdAllowList.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
    }

    if (filters.organisationId) {
        const { data: orgDiaries, error: orgError } = await supabaseClient
            .from("diaries_view")
            .select("diary_id")
            .eq("organisation_id", filters.organisationId)
            .limit(2000);
        if (orgError) throw orgError;
        const orgDiaryIds = (orgDiaries ?? []).map((row) => row.diary_id as string).filter(Boolean);
        if (orgDiaryIds.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
        diaryIdAllowList =
            diaryIdAllowList === null ? orgDiaryIds : diaryIdAllowList.filter((id) => orgDiaryIds.includes(id));
        if (diaryIdAllowList.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
    }

    if (diaryIdAllowList) {
        const trackerIds = await trackerIdsForDiaryAllowList(diaryIdAllowList);
        if (trackerIds.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
        trackerIdAllowList =
            trackerIdAllowList === null ? trackerIds : trackerIdAllowList.filter((id) => trackerIds.includes(id));
        if (trackerIdAllowList.length === 0) {
            return { entries: [], totalCount: 0, page, pageSize };
        }
    }

    let query = supabaseClient.from("entries").select(
        `
            entry_id,
            tracker_id,
            entry_time,
            status,
            submitted_at,
            submission_source,
            timing_label,
            trackers (
                diary_id,
                form_id,
                forms ( form_name ),
                diary:diaries_view (
                    patient_info,
                    organisation_name,
                    organisation_id,
                    patient_first_name,
                    patient_last_name,
                    hospital_number
                )
            )
        `,
        { count: "exact" }
    );

    if (trackerIdAllowList) {
        query = query.in("tracker_id", trackerIdAllowList);
    }
    if (filters.entryId) {
        query = query.eq("entry_id", filters.entryId);
    }
    if (filters.status) {
        query = query.eq("status", filters.status);
    }

    const { data, error, count } = await query
        .order("entry_time", { ascending: false, nullsFirst: false })
        .range(from, to);

    if (error) throw error;

    const entries: AdminEntryRow[] = (data ?? []).map((row) => {
        const tracker = row.trackers as {
            diary_id?: string | null;
            form_id?: string | null;
            forms?: { form_name?: string | null } | null;
            diary?: {
                patient_info?: { patient_name?: string | null } | null;
                organisation_name?: string | null;
                organisation_id?: string | null;
                patient_first_name?: string | null;
                patient_last_name?: string | null;
                hospital_number?: string | null;
            } | null;
        } | null;
        const diary = tracker?.diary ?? null;
        const fromParts = [diary?.patient_first_name, diary?.patient_last_name].filter(Boolean).join(" ").trim();
        return {
            entry_id: row.entry_id as string,
            tracker_id: (row.tracker_id as string | null) ?? null,
            diary_id: tracker?.diary_id ?? null,
            form_id: tracker?.form_id ?? null,
            form_name: tracker?.forms?.form_name ?? null,
            patient_name: diary?.patient_info?.patient_name?.trim() || fromParts || "Unknown patient",
            hospital_number: diary?.hospital_number ?? null,
            organisation_name: diary?.organisation_name ?? null,
            organisation_id: diary?.organisation_id ?? null,
            status: (row.status as string) ?? "draft",
            entry_time: (row.entry_time as string | null) ?? null,
            submitted_at: (row.submitted_at as string | null) ?? null,
            submission_source: (row.submission_source as string | null) ?? null,
            timing_label: (row.timing_label as string | null) ?? null,
        };
    });

    return { entries, totalCount: count ?? entries.length, page, pageSize };
}

export const adminEntriesKey = (filters: AdminEntryFilters) => ["admin-entries", filters] as const;

export function useAdminEntries(filters: AdminEntryFilters) {
    return useQuery({
        queryKey: adminEntriesKey(filters),
        queryFn: () => fetchAdminEntries(filters),
        staleTime: 1000 * 30,
        placeholderData: (previous) => previous,
    });
}
